import type {ColumnFiltersState, SortingState} from '@tanstack/react-table';

import {inject, injectable} from 'inversify';

import type {Nullable} from '../../../utils/utility-types';
import type {YcTable} from '../types';

import {BaseNavigationService} from '../../../core/system/navigation/base/BaseNavigation.service';
import {TableState} from './TableState';

export abstract class TableStorage {
  abstract key: string;
  abstract init(key: string): void;
  abstract read(): YcTable.State;
  abstract write(state: YcTable.State): void;
}

@injectable()
export class TableUrlStorage implements TableStorage {
  key = 'default_table_key';

  constructor(
    @inject(BaseNavigationService)
    private readonly navigationService: BaseNavigationService,
  ) {}

  public init(key: string) {
    this.key = key;

    return this;
  }

  public read(): YcTable.State {
    const navigationParams = this.navigationService.getQueryParams();

    return this.searchParamsToState(navigationParams);
  }

  public write(state: YcTable.State) {
    const params = {
      [this.filterKey]: this.filterToParams(state.columnFilters),
      [this.sortingKey]: this.sortingToParams(state.sorting),
    };

    this.navigationService.writeQueryParams(params);

    return this;
  }

  private get filterKey() {
    return `${this.key}_filter`;
  }

  private get sortingKey() {
    return `${this.key}_sorting`;
  }

  private filterToParams(state: YcTable.ColumnFiltersState): Nullable<string> {
    if (state.length === 0) {
      return null;
    }

    return state.map(({id, value}) => `${id}:${value}`).join(',');
  }

  private searchParamsToState(
    params: BaseNavigationService.QueryParams,
  ): YcTable.State {
    const sorting = this.stringToSorting(params.get(this.sortingKey));
    const columnFilters = this.stringToFilter(params.get(this.filterKey));

    return new TableState().init({
      columnFilters,
      sorting,
    });
  }

  private sortingToParams(state: YcTable.SortingState): Nullable<string> {
    const s = state[0];

    if (!s) {
      return null;
    }

    return `${s.id}:${Number(s.desc)}`;
  }

  private stringToFilter(filterString?: Nullable<string>): ColumnFiltersState {
    if (!filterString) {
      return [];
    }

    const filters = filterString.split(',');

    return filters.map(f => {
      const [id, value] = f.split(':');

      return {id: id!, value: value!};
    });
  }

  private stringToSorting(sortingString?: Nullable<string>): SortingState {
    if (!sortingString) {
      return [];
    }

    const [id, desc] = sortingString.split(':');

    return [{desc: Boolean(desc), id: id!}];
  }
}
